import { getMonitoring } from '@protector/api-client';
import type { SelectedWindow } from 'pages/timeline/timeline.fieldDetails.zustand';

const STORAGE_KEY = 'track-decision-view-later-dismissals';
const TURN_OFF_PROMPT_SHOWN_KEY = 'track-decision-turn-off-prompt-shown';
const X_CLOSE_MARKER = '#x-close';

export const TURN_OFF_REMINDERS_DISMISSAL_THRESHOLD = 3;

const windowKey = ({ areaId, start, end }: SelectedWindow): string => `${areaId}|${start}|${end}`;

const xCloseKey = (windowEntry: string): string => `${windowEntry}${X_CLOSE_MARKER}`;

const isViewLaterEntry = (entry: string): boolean => !entry.endsWith(X_CLOSE_MARKER);

const captureStorageError = (error: unknown): void => {
  getMonitoring().captureError(error, { errorType: 'ClientStateError', domain: 'Timeline' });
};

const readDismissed = (): string[] => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

const writeDismissed = (entries: string[]): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
};

export const dismissWindowForSession = (monitoringWindow: SelectedWindow): void => {
  try {
    const key = windowKey(monitoringWindow);
    const dismissed = readDismissed();
    const withoutMarker = dismissed.filter(entry => entry !== xCloseKey(key));
    if (withoutMarker.includes(key) && withoutMarker.length === dismissed.length) return;
    writeDismissed(withoutMarker.includes(key) ? withoutMarker : [...withoutMarker, key]);
  } catch (error) {
    captureStorageError(error);
  }
};

export const dismissWindowForSessionOnClose = (monitoringWindow: SelectedWindow): void => {
  try {
    const key = windowKey(monitoringWindow);
    const dismissed = readDismissed();
    if (dismissed.includes(key) || dismissed.includes(xCloseKey(key))) return;
    writeDismissed([...dismissed, xCloseKey(key)]);
  } catch (error) {
    captureStorageError(error);
  }
};

export const isWindowDismissedForSession = (monitoringWindow?: SelectedWindow): boolean => {
  if (!monitoringWindow) return false;
  const key = windowKey(monitoringWindow);
  const dismissed = readDismissed();
  return dismissed.includes(key) || dismissed.includes(xCloseKey(key));
};

export const markTurnOffPromptAsShownForSession = (): void => {
  try {
    sessionStorage.setItem(TURN_OFF_PROMPT_SHOWN_KEY, 'true');
  } catch (error) {
    captureStorageError(error);
  }
};

const wasTurnOffPromptShownInSession = (): boolean => {
  try {
    return sessionStorage.getItem(TURN_OFF_PROMPT_SHOWN_KEY) === 'true';
  } catch {
    return false;
  }
};

export const shouldOfferTurnOffReminders = (): boolean =>
  readDismissed().filter(isViewLaterEntry).length >= TURN_OFF_REMINDERS_DISMISSAL_THRESHOLD && !wasTurnOffPromptShownInSession();

export const clearTrackDecisionSessionState = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(TURN_OFF_PROMPT_SHOWN_KEY);
  } catch (error) {
    captureStorageError(error);
  }
};
